import React, { MutableRefObject, SetStateAction, useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import dayjs from 'dayjs';
import { ListEvent } from './list-event';
import { MonthPagination } from './month-pagination';
import { appendMonth, setCurrentDate } from '../features/calendar/calendar-slice';
import { useAppDispatch } from '../app/store';
import { useAfterPaintEffect, useAppSelector } from '../app/hooks';
import { Month, Day, CombinedFilterData, FilterType, FilterResponse } from '../helpers/common-types';
import { DATE_FORMAT, isInView } from '../helpers/date-helpers';
import { useCurrentScrolledItem } from '../app/date-scroll-hook';
import { SvgIcon } from './svg-icon';
import { writeStateToUrl } from '../app/query-string';

export type ListProps = {
    filteredEvents: Month[];
    combinedFilterData: CombinedFilterData;
}

export function ListLayout(props: ListProps) {
    const { filteredEvents, combinedFilterData } = props;
    const dispatch = useAppDispatch();
    const emptyMonthMessaging = useAppSelector(state => state.settings.emptyMonthMessaging);
    const currentDate = useAppSelector(state => state.calendar.currentDate);
    const [firstEventInRange, setFirstEventInRange] = useState('');
    const filterBarHeight = useAppSelector(state => state.calendar.filterBarHeight);
    const appendMonths = useAppSelector(state => state.calendar.appendMonths);
    const eventsHaveLoaded = useAppSelector(state => state.calendar.eventsHaveLoaded);

    const { currentScrolledItem, browserWidth } = useCurrentScrolledItem(firstEventInRange, filterBarHeight);

    /*
     * Get first date with events so the currentDate has an initial value when you switch months
     */
    const getFirstEventInRange = useCallback(() => {
        const firstDay = filteredEvents?.find(month => month.anyEvents)?.days.find(day => day.events.length > 0);
        return firstDay?.date || '';
    }, [filteredEvents[0].month]);

    /*
     * Filter the days so we don't try to render more days than necessary
     */
    const getDaysWithEvents = useCallback((allDays: Day[]) => {
        return allDays.filter(day => day.events?.length > 0);
    }, [filteredEvents[0].month]);

    useEffect(() => {
        setFirstEventInRange(getFirstEventInRange());
    }, [getFirstEventInRange]);

    /*
     * Set currentDate when scrolled to a new date
     */
    useEffect(() => {
        if (currentScrolledItem !== '') {
            dispatch(setCurrentDate({ newDate: currentScrolledItem, shouldChangeRange: false }));
        }
    }, [currentScrolledItem]);

    useEffect(() => {
        if (firstEventInRange !== '') {
            dispatch(setCurrentDate({ newDate: firstEventInRange, shouldChangeRange: false }));
        }
    }, [firstEventInRange]);

    /*
     * Scroll to date when a currentDate is set via the calendar or filters
     */
    useAfterPaintEffect(() => {
        if (currentDate !== currentScrolledItem && eventsHaveLoaded) {
            // Attempt to locate exact date in list. If that doesn't exist, go to the heading at the start of the month.
            let currentDateKey = document.querySelector(`[data-date-key="${currentDate}"]`);
            if (!currentDateKey) {
                currentDateKey = document.querySelector(`[data-date-key="${dayjs(currentDate).startOf('month').format(DATE_FORMAT)}"]`);
            }
            if (currentDateKey) {
                if (!isInView(currentDateKey)) {
                    currentDateKey.scrollIntoView({ block: "start" });
                    (currentDateKey as HTMLElement).focus({ preventScroll: true });
                }
            }
        }
    }, [currentDate, eventsHaveLoaded]);

    const categoryFilterOptions = combinedFilterData[FilterType.CATEGORIES] || null;

    return (
        <div className="ace-cal-list-column">
            <div className={`ace-cal-list ${appendMonths ? 'ace-cal-list-months' : ''}`}>
                {
                    filteredEvents.map((monthData, monthIndex) => (
                        <section className="ace-cal-list-month" key={monthIndex}>
                            <h2 id={`Month-List-${dayjs(monthData.month).format('DD-MM-YYYY')}`} className="ace-cal-list-month-name" data-date-key={monthData.month} tabIndex={-1}>
                                {dayjs(monthData.month).format('MMMM YYYY')}
                            </h2>
                            <MonthPagination />
                            {
                                monthData.anyEvents ?
                                    <ul className="ace-cal-list-days">
                                        {
                                            getDaysWithEvents(monthData.days).map((dayItem, dayIndex) => (
                                                <ListDay {...{ dayItem, dayIndex, categoryFilterOptions, browserWidth }} />
                                            ))
                                        }
                                    </ul>
                                    :
                                    <div className="ace-cal-list-days-empty space-top-lg">
                                        <div dangerouslySetInnerHTML={{ __html: emptyMonthMessaging }} />
                                    </div>
                            }
                        </section>
                    ))
                }
            </div>

        </div>
    )
}

const ListDay = (props: { dayItem: Day, dayIndex: number, categoryFilterOptions: FilterResponse[] | null, browserWidth: number }) => {
    const { dayItem, dayIndex, categoryFilterOptions, browserWidth } = props;
    const currentDate = useAppSelector(state => state.calendar.currentDate);
    const dayDate = dayjs(dayItem.date).format(DATE_FORMAT);
    const stickyHeading = useRef<HTMLHeadingElement>(null);
    const [headingOffset, setHeadingOffset] = useState(0);
    const [showShareTooltip, setShowShareTooltip] = useState(false);
    const { shareLinkCopiedTooltip, shareDateButtonText, showShareButtons } = useAppSelector(state => state.settings);

    useEffect(() => {
        setHeadingOffset(stickyHeading?.current?.offsetHeight || 0);
    }, [browserWidth]);

    const shareClicked = () => {
        const shareUrl = writeStateToUrl(dayDate);
        navigator.clipboard.writeText(shareUrl);
        setShowShareTooltip(true);
        setTimeout(() => {
            setShowShareTooltip(false);
        }, 3000);
    }

    return (
        <li className={`ace-cal-list-day ${currentDate === dayDate ? "ace-cal-list-day-active" : ""}`}
            key={dayIndex}
            data-date={dayDate}
            data-date-key={dayDate}
            tabIndex={-1}
            aria-labelledby={`Date_${dayDate}`}
            style={{ '--heading-offset': `${headingOffset}px` } as React.CSSProperties}>
            <h3 className="ace-cal-list-day-date spaced-list align-items-center h5-style" id={`Date_${dayDate}`} ref={stickyHeading}>
                <span>{dayjs(dayItem.date).format("dddd, MMMM D")}</span>
                {
                    showShareButtons &&
                    <button onClick={shareClicked} className={`btn-secondary icon-btn-sm btn-round btn-subtle ${showShareTooltip ? "show-tooltip" : ""}`} type="button" data-tooltip={shareLinkCopiedTooltip}>
                        <SvgIcon iconId="icon-link" />
                        <span className="visually-hidden">{shareDateButtonText}</span>
                    </button>
                }
            </h3>
            <ul className="ace-cal-list-day-content">
                {
                    dayItem.events.map(event => (
                        <ListEvent {...{ event, categoryFilterOptions }} key={event.id} />
                    ))
                }
            </ul>
        </li>
    )
}